import { ArrowRight, Ban, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { SupplyOrderState, TotadminPermission } from 'data/__generated__';

import { getColorsFromState, SUPPLY_ORDER_STATES_LABELS } from '../../constants/states';
import { SupplyOrderStateAction } from '../useSupplyOrderStateActions';

/**
 * Le statut de l'achat et, à côté, les seules transitions possibles depuis là.
 *
 * L'ancienne fiche affichait les neuf statuts en permanence, sept d'entre eux grisés : il fallait les
 * essayer pour savoir lesquels étaient ouverts. Ici on ne montre que ce qui est faisable.
 */
export const SupplyOrderStateCard = ({
    state,
    actions,
    loading,
    isDirty,
}: {
    state: SupplyOrderState;
    actions: SupplyOrderStateAction[];
    loading: boolean;
    isDirty: boolean;
}) => {
    const { backgroundColor, labelColor } = getColorsFromState(state) ?? {};
    const nextActions = actions.filter(({ state: targetState }) => targetState !== SupplyOrderState.Aborted);
    const abortAction = actions.find(({ state: targetState }) => targetState === SupplyOrderState.Aborted);

    return (
        <div
            className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-border bg-card px-4 py-3"
            data-test="supply-order-state-card"
        >
            <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Statut</span>
                <Badge
                    className="rounded-full border-0 px-4 py-1 text-sm font-medium"
                    style={{ backgroundColor: `${backgroundColor}dd`, color: labelColor }}
                    data-test="supply-order-state"
                >
                    {SUPPLY_ORDER_STATES_LABELS[state]}
                </Badge>
            </div>

            {nextActions.length || abortAction ? (
                <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            ) : null}

            <StateActionsTooltip isDirty={isDirty}>
                <div className="flex flex-wrap items-center gap-2">
                    {nextActions.map(({ state: targetState, label, run }) => (
                        <Button
                            key={targetState}
                            type="button"
                            variant="outline"
                            disabled={isDirty || loading}
                            requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                            onClick={() => void run()}
                            data-test={`supply-order-state-action-${targetState}`}
                        >
                            {loading ? <Loader2 className="animate-spin" /> : null}
                            {label}
                        </Button>
                    ))}
                    {abortAction ? (
                        <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            disabled={isDirty || loading}
                            requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                            onClick={() => void abortAction.run()}
                            data-test="supply-order-state-action-Aborted"
                        >
                            <Ban />
                            {abortAction.label}
                        </Button>
                    ) : null}
                    {!nextActions.length && !abortAction ? (
                        <span className="text-sm text-muted-foreground">Aucune action possible depuis ce statut.</span>
                    ) : null}
                </div>
            </StateActionsTooltip>
        </div>
    );
};

/**
 * Un changement de statut relit l'achat en base : le faire avec des modifications non enregistrées les
 * perdrait sans prévenir. On dit pourquoi c'est bloqué plutôt que de désactiver en silence.
 */
const StateActionsTooltip = ({ isDirty, children }: { isDirty: boolean; children: React.ReactNode }) => {
    if (!isDirty) {
        return <>{children}</>;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div>{children}</div>
            </TooltipTrigger>
            <TooltipContent>Enregistrez ou réinitialisez vos modifications avant de changer le statut.</TooltipContent>
        </Tooltip>
    );
};
